import { Eye, EyeOff, Trash2, X } from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";
import { StrokeStyleControl } from "../reusable/StrokeStyleControl";
import { isMultiShapeSelection } from "./SvgDiagram2DMultiSelectPanel";

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

/**
 * Floating inspector for the selected line: edits its two endpoints and stroke
 * style. Rendered as an overlay inside the viewer (mirrors the ellipse
 * inspector); closes itself when no line is selected.
 */
export function SvgDiagram2DLinePanel() {
    const ctx = useSvgDiagram2D();
    const { doc, selectedLineIds, setSelectedLineId, updateLine, removeLine, pushSnapshot } =
        ctx;

    // A multi-shape selection is handled by the shared multi-select panel.
    if (isMultiShapeSelection(ctx)) return null;

    // The inspector edits the first selected line (matches the ellipse panel).
    const line = doc.lines.find((l) => l.id === selectedLineIds[0]);
    if (!line) return null;

    /** Snapshot before a discrete (single-commit) edit so it can be undone. */
    const edit = (partial: Parameters<typeof updateLine>[1]) => {
        pushSnapshot();
        updateLine(line.id, partial);
    };

    return (
        <div className="diagram-node-panel">
            <div className="field-row" style={{ alignItems: "center", gap: 6 }}>
                <strong style={{ flex: 1, minWidth: 0 }}>Line properties</strong>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => setSelectedLineId(null)}
                    title="Close panel"
                    style={{ padding: "2px 6px" }}
                >
                    <X size={14} aria-hidden />
                </button>
            </div>

            {/* --- Geometry --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Geometry</p>
                <label className="field-label">
                    Label
                    <input
                        type="text"
                        value={line.label}
                        onChange={(e) =>
                            updateLine(line.id, { label: e.target.value })
                        }
                    />
                </label>
                {(["start", "end"] as const).map((end) => (
                    <div key={end} className="field-row field-row--two">
                        {(["x", "y"] as const).map((axis) => (
                            <label key={axis} className="field-label">
                                {`${end === "start" ? "Start" : "End"} ${axis.toUpperCase()}`}
                                <DebouncedInput
                                    type="number"
                                    step={1}
                                    value={line[end][axis]}
                                    onChange={(v) =>
                                        edit({
                                            [end]: {
                                                ...line[end],
                                                [axis]: num(v, line[end][axis]),
                                            },
                                        })
                                    }
                                />
                            </label>
                        ))}
                    </div>
                ))}
            </div>

            {/* --- Style --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Style</p>
                <StrokeStyleControl
                    strokeStyleId={line.strokeStyleId}
                    strokeStyles={doc.strokeStyles}
                    onChange={(strokeStyleId) => edit({ strokeStyleId })}
                >
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Stroke
                            <input
                                type="color"
                                value={line.color}
                                onChange={(e) =>
                                    updateLine(line.id, {
                                        color: e.target.value,
                                    })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Width
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                step={0.1}
                                value={line.strokeWidth}
                                onChange={(v) =>
                                    edit({
                                        strokeWidth: Math.max(
                                            0.1,
                                            num(v, line.strokeWidth),
                                        ),
                                    })
                                }
                            />
                        </label>
                    </div>
                    <label className="field-label">
                        Dash (e.g. "4 2", empty = solid)
                        <input
                            type="text"
                            value={line.dash}
                            onChange={(e) =>
                                updateLine(line.id, { dash: e.target.value })
                            }
                        />
                    </label>
                </StrokeStyleControl>
                <CheckboxField
                    checked={line.visible}
                    onChange={(checked) =>
                        updateLine(line.id, { visible: checked })
                    }
                    label={
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                            }}
                        >
                            {line.visible ? (
                                <Eye size={14} aria-hidden />
                            ) : (
                                <EyeOff size={14} aria-hidden />
                            )}
                            Visible
                        </span>
                    }
                    inline
                />
            </div>

            {/* --- Danger --- */}
            <div className="diagram-node-panel-section">
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removeLine(line.id)}
                    title="Delete this line"
                >
                    <Trash2 size={14} aria-hidden /> Delete line
                </button>
            </div>
        </div>
    );
}
